// ABOUTME: Form for Order Intake Lead Time configuration — Thresholds, Standard Lead Time, Non-Standard sections.
// ABOUTME: Uses react-hook-form; renders DayChipToggle for applicable days, NumericalInput + FilterDropdown for lead times.
import {Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline} from '@mdi/js'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {DAYS, DayIdentifier} from '../types/deliveryWindow'
import {
  CALCULATION_METHODS,
  CalculationMethod,
  DAY_FULL_NAME_KEYS,
  NonStandardLeadTimeFormValues,
  OrderIntakeLeadTimeConfig,
  OrderIntakeLeadTimeFormValues,
  REFERENCE_EVENTS,
  ReferenceEvent,
  StandardLeadTimeFormValues,
  TIME_UOM_OPTIONS,
  fromDayIdentifier,
  toDayIdentifier,
  toFormValues,
} from '../types/orderIntakeLeadTime'
import {DayChipToggle} from './DayChipToggle'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'
import {SectionTitle} from './SectionTitle'
import {normalizeLeadTimeDays} from '../utils/normalizeLeadTimeDays'
import {withFallback} from '../utils/entityCatalogOptions'

export interface LeadTimeFormOptions {
  /** Allowed editable fields from the catalog `leadTime.availableFields`. */
  availableFields?: string[]
  /** Allowed reference events from the catalog `leadTime.availableReferenceEvents`. */
  referenceEvents?: ReferenceEvent[]
  /** Allowed calculation methods from the catalog `leadTime.availableCalculationMethods`. */
  calculationMethods?: CalculationMethod[]
  /** Lead-time UoM options from the catalog `leadTime.availableUnitsOfMeasure`. */
  timeUomOptions?: {value: string; label: string}[]
}

export interface OrderIntakeLeadTimeFormProps {
  config: OrderIntakeLeadTimeConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: OrderIntakeLeadTimeFormValues) => void
  /** Available distance UoM options for the far distance threshold dropdown */
  distanceUomOptions: {value: string; label: string}[]
  /** Backend-driven lead-time option lists. Each field falls back to the hardcoded constant. */
  leadTimeOptions?: LeadTimeFormOptions
  /** Days that are not business days — shown as disabled with tooltip in DayChipToggle */
  nonBusinessDays?: DayIdentifier[]
  /** Test ID for Cypress — defaults to "order-intake-lead-time-form" */
  'data-cy'?: string
}

const NO_NON_BUSINESS_DAYS: DayIdentifier[] = []

export const OrderIntakeLeadTimeForm: React.FC<OrderIntakeLeadTimeFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  distanceUomOptions,
  leadTimeOptions,
  nonBusinessDays = NO_NON_BUSINESS_DAYS,
  'data-cy': dataCy = 'order-intake-lead-time-form',
}) => {
  const {t} = useTranslation()

  const businessDays = useMemo(
    () => DAYS.filter((d) => !nonBusinessDays.includes(d)),
    [nonBusinessDays],
  )

  // --- Options derived from the catalog (with fallback to the hardcoded constants) ---

  const referenceEventValues = useMemo<ReferenceEvent[]>(
    () => withFallback(leadTimeOptions?.referenceEvents, REFERENCE_EVENTS),
    [leadTimeOptions],
  )

  const calculationMethodValues = useMemo<CalculationMethod[]>(
    () => withFallback(leadTimeOptions?.calculationMethods, CALCULATION_METHODS),
    [leadTimeOptions],
  )

  const timeUomOptions = useMemo(
    () => withFallback(leadTimeOptions?.timeUomOptions, TIME_UOM_OPTIONS),
    [leadTimeOptions],
  )

  const formDefaultValues = useMemo(() => {
    const vals = toFormValues(config)
    const {changed, standard, nonStandard} = normalizeLeadTimeDays(vals, nonBusinessDays)
    const normalized = changed ? {...vals, standard, nonStandard} : vals
    if (!normalized.farDistanceThresholdUomId && distanceUomOptions.length > 0) {
      normalized.farDistanceThresholdUomId = distanceUomOptions[0].value
      normalized.farDistanceThresholdUomDisplay = distanceUomOptions[0].label
    }

    if (normalized.standard) return normalized

    // Seed with catalog-allowed values so a new config doesn't start with a rejected UoM/event.
    const defaultUom = timeUomOptions.find((o) => o.value === 'businessMinute') ?? timeUomOptions[0]
    const defaultEvent: ReferenceEvent = referenceEventValues.includes('beforeRequestedDeliveryTime')
      ? 'beforeRequestedDeliveryTime'
      : referenceEventValues[0]

    // No standard section in the config — seed one over the business days not covered
    // by non-standard entries.
    const sampleDay = normalized.nonStandard[0]?.day
    const usesCodes = sampleDay ? (sampleDay.length <= 3 && sampleDay === sampleDay.toUpperCase()) : false
    const nonStandardDayIds = new Set(
      normalized.nonStandard
        .map((ns) => toDayIdentifier(ns.day))
        .filter((d): d is DayIdentifier => d !== undefined),
    )
    return {
      ...normalized,
      standard: {
        applicableDays: businessDays
          .filter((id) => !nonStandardDayIds.has(id))
          .map((id) => fromDayIdentifier(id, usesCodes)),
        baseLeadTime: 0,
        baseLeadTimeUomId: defaultUom?.value ?? '',
        baseLeadTimeUomDisplay: defaultUom?.label ?? '',
        referenceEvent: defaultEvent,
        farDistanceModifier: 0,
        farDistanceModifierUomId: defaultUom?.value ?? '',
        farDistanceModifierUomDisplay: defaultUom?.label ?? '',
        calculationMethod: 'basePlusAllModifiers' as CalculationMethod,
      } satisfies StandardLeadTimeFormValues,
    }
  }, [config, businessDays, nonBusinessDays, distanceUomOptions, timeUomOptions, referenceEventValues])

  const {control, setValue, reset, getValues, formState: {isDirty}} = useForm<OrderIntakeLeadTimeFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const farDistanceThreshold = useWatch({control, name: 'farDistanceThreshold'})
  const farDistanceThresholdUomId = useWatch({control, name: 'farDistanceThresholdUomId'})
  const farDistanceThresholdUomDisplay = useWatch({control, name: 'farDistanceThresholdUomDisplay'})
  const standard = useWatch({control, name: 'standard'})
  const nonStandard = useWatch({control, name: 'nonStandard'})

  const values = useMemo<OrderIntakeLeadTimeFormValues>(() => ({
    farDistanceThreshold,
    farDistanceThresholdUomId,
    farDistanceThresholdUomDisplay,
    standard,
    nonStandard,
  }), [farDistanceThreshold, farDistanceThresholdUomId, farDistanceThresholdUomDisplay, standard, nonStandard])

  const errors = useMemo<Partial<Record<string, string>>>(() => {
    const errs: Partial<Record<string, string>> = {}
    const allowedUoms = new Set(timeUomOptions.map((o) => o.value))
    const isAllowedRule = (rule: {baseLeadTimeUomId: string; farDistanceModifierUomId: string; referenceEvent: ReferenceEvent}) =>
      allowedUoms.has(rule.baseLeadTimeUomId) &&
      allowedUoms.has(rule.farDistanceModifierUomId) &&
      referenceEventValues.includes(rule.referenceEvent)

    if (farDistanceThreshold <= 0) {
      errs.farDistanceThreshold = t('countryConfigurations.leadTimes.errorPositive')
    }
    if (!farDistanceThresholdUomId) {
      errs.farDistanceThresholdUomId = t('countryConfigurations.leadTimes.errorUomRequired')
    }
    if (standard) {
      if (standard.applicableDays.length === 0) {
        errs.applicableDays = t('countryConfigurations.leadTimes.errorDaysRequired')
      }
      if (standard.baseLeadTime < 0) {
        errs.baseLeadTime = t('countryConfigurations.leadTimes.errorNonNegative')
      }
      if (standard.farDistanceModifier < 0) {
        errs.farDistanceModifier = t('countryConfigurations.leadTimes.errorNonNegative')
      }
      if (!isAllowedRule(standard)) {
        errs.standardOptions = t('countryConfigurations.leadTimes.errorNotAllowed')
      }
    }
    nonStandard.forEach((ns, i) => {
      if (ns.baseLeadTime < 0) {
        errs[`nonStandard_${i}_baseLeadTime`] = t('countryConfigurations.leadTimes.errorNonNegative')
      }
      if (ns.farDistanceModifier < 0) {
        errs[`nonStandard_${i}_farDistanceModifier`] = t('countryConfigurations.leadTimes.errorNonNegative')
      }
      if (!isAllowedRule(ns)) {
        errs.nonStandardOptions = t('countryConfigurations.leadTimes.errorNotAllowed')
      }
    })
    return errs
  }, [farDistanceThreshold, farDistanceThresholdUomId, standard, nonStandard, timeUomOptions, referenceEventValues, t])

  const isValid = Object.keys(errors).length === 0

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    onValuesChange?.(values)
  }, [values, onValuesChange])

  const referenceEventOptions = useMemo(
    () => referenceEventValues.map((ev) => ({
      value: ev,
      label: t(`countryConfigurations.leadTimes.referenceEvents.${ev}`),
    })),
    [referenceEventValues, t],
  )

  const calculationMethodOptions = useMemo(
    () => calculationMethodValues.map((m) => ({
      value: m,
      label: t(`countryConfigurations.leadTimes.calculationMethods.${m}`),
    })),
    [calculationMethodValues, t],
  )

  // --- Day conversion helpers ---

  /** Detect whether the backend uses 3-letter codes (e.g. "MON") or full names (e.g. "monday") */
  const backendUsesCodes = useMemo(() => {
    const firstDay = formDefaultValues.standard?.applicableDays[0] ?? formDefaultValues.nonStandard[0]?.day
    if (!firstDay) return false
    return firstDay.length <= 3 && firstDay === firstDay.toUpperCase()
  }, [formDefaultValues])

  // Runtime guard: if the resolved business days change after load, re-normalize the
  // current values (prune non-business days, fill missing ones) in a single reset.
  // Gated on nonBusinessDays and the loaded config — not on every keystroke.
  useEffect(() => {
    const current = getValues()
    const {changed, standard: normalizedStandard, nonStandard: normalizedNonStandard} =
      normalizeLeadTimeDays(current, nonBusinessDays)
    if (changed) {
      reset({...current, standard: normalizedStandard, nonStandard: normalizedNonStandard})
    }
  }, [nonBusinessDays, formDefaultValues, getValues, reset])

  const applicableDayIds = useMemo<DayIdentifier[]>(() => {
    if (!standard) return []
    return standard.applicableDays
      .map(toDayIdentifier)
      .filter((d): d is DayIdentifier => d !== undefined)
  }, [standard])

  const handleApplicableDaysChange = useCallback(
    (newDayIds: DayIdentifier[]) => {
      if (!standard) return
      const newDays = newDayIds.map((id) => fromDayIdentifier(id, backendUsesCodes))
      setValue('standard', {...standard, applicableDays: newDays}, {shouldDirty: true})

      // Remove non-standard entries for days that are now standard
      const removedNonStandard = nonStandard.filter((ns) => {
        const nsId = toDayIdentifier(ns.day)
        return nsId !== undefined && !newDayIds.includes(nsId)
      })

      // Add non-standard entries for days that were deselected
      const previousIds = new Set(applicableDayIds)
      const deselected = DAYS.filter((d) => previousIds.has(d) && !newDayIds.includes(d))
      const newEntries: NonStandardLeadTimeFormValues[] = deselected.map((dayId) => ({
        day: fromDayIdentifier(dayId, backendUsesCodes),
        baseLeadTime: standard.baseLeadTime,
        baseLeadTimeUomId: standard.baseLeadTimeUomId,
        baseLeadTimeUomDisplay: standard.baseLeadTimeUomDisplay,
        referenceEvent: standard.referenceEvent,
        farDistanceModifier: standard.farDistanceModifier,
        farDistanceModifierUomId: standard.farDistanceModifierUomId,
        farDistanceModifierUomDisplay: standard.farDistanceModifierUomDisplay,
        calculationMethod: standard.calculationMethod,
      }))

      const combined = [...removedNonStandard, ...newEntries]
      const sorted = [...combined].sort((a, b) => {
        const aIdx = DAYS.indexOf(toDayIdentifier(a.day) as DayIdentifier)
        const bIdx = DAYS.indexOf(toDayIdentifier(b.day) as DayIdentifier)
        return (aIdx === -1 ? DAYS.length : aIdx) - (bIdx === -1 ? DAYS.length : bIdx)
      })
      setValue('nonStandard', sorted, {shouldDirty: true})
    },
    [standard, nonStandard, applicableDayIds, backendUsesCodes, setValue],
  )

  const getDayFullName = useCallback(
    (day: string): string => {
      const key = DAY_FULL_NAME_KEYS[day.toLowerCase()]
      return key ? t(key) : day
    },
    [t],
  )

  // --- Summary text helpers ---

  const getReferenceEventLabel = useCallback(
    (ev: ReferenceEvent) => t(`countryConfigurations.leadTimes.referenceEvents.${ev}`),
    [t],
  )

  const getUomLabel = useCallback((uomId: string, uomDisplay: string) => {
    const opt = timeUomOptions.find((o) => o.value === uomId)
    return opt?.label ?? uomDisplay
  }, [timeUomOptions])

  const buildSummaryText = useCallback(
    (section: 'standardLeadTime' | 'nonStandardLeadTimes', baseLeadTime: number, baseLeadTimeUomId: string, baseLeadTimeUomDisplay: string, referenceEvent: ReferenceEvent, farDistanceModifier: number, farDistanceModifierUomId: string, farDistanceModifierUomDisplay: string) => {
      const baseUom = getUomLabel(baseLeadTimeUomId, baseLeadTimeUomDisplay)
      const modUom = getUomLabel(farDistanceModifierUomId, farDistanceModifierUomDisplay)
      const refLabel = getReferenceEventLabel(referenceEvent)
      const standardText = t(`countryConfigurations.leadTimes.${section}.summaryStandardText`, {baseLeadTime, baseUom, refLabel})
      const farDistanceText = t(`countryConfigurations.leadTimes.${section}.summaryFarDistanceText`, {farDistanceModifier, modUom, baseLeadTime, baseUom, refLabel})
      return {standardText, farDistanceText}
    },
    [getUomLabel, getReferenceEventLabel, t],
  )

  // --- Non-standard field updater ---

  const updateNonStandardField = useCallback(
    (index: number, field: keyof NonStandardLeadTimeFormValues, value: string | number) => {
      const updated = [...nonStandard]
      updated[index] = {...updated[index], [field]: value}
      setValue('nonStandard', updated, {shouldDirty: true})
    },
    [nonStandard, setValue],
  )

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy={dataCy}>
      {/* ──── Section 1: Thresholds ──── */}
      <SectionTitle titleKey="countryConfigurations.leadTimes.thresholds.title" />

      <div className="flex gap-8 items-start w-full">
        <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
          <div className="flex gap-0.5 items-center whitespace-nowrap">
            <TextMedium className="text-foreground">
              {t('countryConfigurations.leadTimes.thresholds.farDistanceThreshold')}
            </TextMedium>
            <CountryConfigsTooltip
              content={t('countryConfigurations.leadTimes.thresholds.farDistanceThresholdTooltip')}
              delayMs={300}
            >
              <span className="text-foreground flex items-center cursor-default">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
          </div>
        </div>
        <div className="flex gap-1 items-start">
          <NumericalInput
            value={farDistanceThreshold}
            onChange={(val) => setValue('farDistanceThreshold', val, {shouldDirty: true})}
            min={0}
            disabled={isReadOnly}
            error={errors.farDistanceThreshold}
            data-cy="threshold-far-distance-value"
          />
          <FilterDropdown
            value={farDistanceThresholdUomId}
            onChange={(val) => {
              setValue('farDistanceThresholdUomId', val, {shouldDirty: true})
              const selected = distanceUomOptions.find((o) => o.value === val)
              if (selected) {
                setValue('farDistanceThresholdUomDisplay', selected.label, {shouldDirty: true})
              }
            }}
            options={distanceUomOptions}
            size="small"
            disabled={isReadOnly}
            data-cy="threshold-far-distance-uom"
          />
        </div>
        {errors.farDistanceThresholdUomId && (
          <p role="alert" className="text-xs leading-4 text-negative px-3">{errors.farDistanceThresholdUomId}</p>
        )}
      </div>

      <hr className="border-border" />

      {/* ──── Section 2: Standard Lead Time ──── */}
      {standard && (
        <>
          <SectionTitle titleKey="countryConfigurations.leadTimes.standardLeadTime.title" />
          {errors.standardOptions && (
            <p role="alert" className="text-xs leading-4 text-negative">{errors.standardOptions}</p>
          )}

          {/* Applicable days */}
          <div className="flex gap-8 items-start w-full">
            <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                <TextMedium className="text-foreground">
                  {t('countryConfigurations.leadTimes.standardLeadTime.applicableDays')}
                </TextMedium>
                <CountryConfigsTooltip
                  content={t('countryConfigurations.leadTimes.standardLeadTime.applicableDaysTooltip')}
                  delayMs={300}
                >
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <DayChipToggle
                value={applicableDayIds}
                onChange={handleApplicableDaysChange}
                disabled={isReadOnly}
                disabledDays={nonBusinessDays}
                disabledTooltip={t('countryConfigurations.leadTimes.standardLeadTime.nonBusinessDayTooltip')}
                data-cy="standard-applicable-days"
              />
              {errors.applicableDays && (
                <p role="alert" className="text-xs leading-4 text-negative">{errors.applicableDays}</p>
              )}
            </div>
          </div>

          {/* Base lead time */}
          <div className="flex gap-8 items-start w-full">
            <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                <TextMedium className="text-foreground">
                  {t('countryConfigurations.leadTimes.standardLeadTime.baseLeadTime')}
                </TextMedium>
                <CountryConfigsTooltip
                  content={t('countryConfigurations.leadTimes.standardLeadTime.baseLeadTimeTooltip')}
                  delayMs={300}
                >
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
            </div>
            <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
              <NumericalInput
                value={standard.baseLeadTime}
                onChange={(val) => setValue('standard', {...standard, baseLeadTime: val}, {shouldDirty: true})}
                min={0}
                disabled={isReadOnly}
                data-cy="standard-base-lead-time-value"
              />
              <FilterDropdown
                value={standard.baseLeadTimeUomId}
                onChange={(val) => {
                  const opt = timeUomOptions.find((o) => o.value === val)
                  setValue('standard', {
                    ...standard,
                    baseLeadTimeUomId: val,
                    baseLeadTimeUomDisplay: opt?.label ?? val,
                  }, {shouldDirty: true})
                }}
                options={timeUomOptions}
                size="small"
                disabled={isReadOnly}
                data-cy="standard-base-lead-time-uom"
              />
              <FilterDropdown
                value={standard.referenceEvent}
                onChange={(val) => setValue('standard', {...standard, referenceEvent: val as ReferenceEvent}, {shouldDirty: true})}
                options={referenceEventOptions}
                size="medium"
                disabled={isReadOnly}
                data-cy="standard-reference-event"
              />
            </div>
          </div>

          {/* Far distance modifier + Calculation method — shared width container */}
          <div className="flex flex-col gap-6 w-max">
            {/* Far distance modifier */}
            <div className="flex gap-8 items-start">
              <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
                <div className="flex gap-0.5 items-center whitespace-nowrap">
                  <TextMedium className="text-foreground">
                    {t('countryConfigurations.leadTimes.standardLeadTime.farDistanceModifier')}
                  </TextMedium>
                  <CountryConfigsTooltip
                    content={t('countryConfigurations.leadTimes.standardLeadTime.farDistanceModifierTooltip')}
                    delayMs={300}
                  >
                    <span className="text-foreground flex items-center cursor-default">
                      <Icon path={mdiInformationOutline} size="regular" />
                    </span>
                  </CountryConfigsTooltip>
                </div>
              </div>
              <div className="flex gap-1 items-start">
                <NumericalInput
                  value={standard.farDistanceModifier}
                  onChange={(val) => setValue('standard', {...standard, farDistanceModifier: val}, {shouldDirty: true})}
                  min={0}
                  disabled={isReadOnly}
                  data-cy="standard-far-distance-modifier-value"
                />
                <FilterDropdown
                  value={standard.farDistanceModifierUomId}
                  onChange={(val) => {
                    const opt = timeUomOptions.find((o) => o.value === val)
                    setValue('standard', {
                      ...standard,
                      farDistanceModifierUomId: val,
                      farDistanceModifierUomDisplay: opt?.label ?? val,
                    }, {shouldDirty: true})
                  }}
                  options={timeUomOptions}
                  size="small"
                  disabled={isReadOnly}
                  data-cy="standard-far-distance-modifier-uom"
                />
              </div>
            </div>

            {/* Calculation method */}
            <div className="flex gap-8 items-start w-full">
              <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
                <div className="flex gap-0.5 items-center whitespace-nowrap">
                  <TextMedium className="text-foreground">
                    {t('countryConfigurations.leadTimes.standardLeadTime.calculationMethod')}
                  </TextMedium>
                  <CountryConfigsTooltip
                    content={t('countryConfigurations.leadTimes.standardLeadTime.calculationMethodTooltip')}
                    delayMs={300}
                  >
                    <span className="text-foreground flex items-center cursor-default">
                      <Icon path={mdiInformationOutline} size="regular" />
                    </span>
                  </CountryConfigsTooltip>
                </div>
              </div>
              <div className="flex-1">
                <FilterDropdown
                  value={standard.calculationMethod}
                  onChange={(val) => setValue('standard', {...standard, calculationMethod: val as CalculationMethod}, {shouldDirty: true})}
                  options={calculationMethodOptions}
                  size="medium"
                  disabled={isReadOnly}
                  data-cy="standard-calculation-method"
                />
              </div>
            </div>
          </div>

          {/* Standard summary */}
          {(() => {
            const {standardText, farDistanceText} = buildSummaryText(
              'standardLeadTime',
              standard.baseLeadTime, standard.baseLeadTimeUomId, standard.baseLeadTimeUomDisplay,
              standard.referenceEvent, standard.farDistanceModifier, standard.farDistanceModifierUomId,
              standard.farDistanceModifierUomDisplay,
            )
            return (
              <div className="text-foreground text-base leading-6">
                <p className="italic">{t('countryConfigurations.leadTimes.standardLeadTime.summaryLabel')}</p>
                <p><span className="font-semibold">{t('countryConfigurations.leadTimes.standardLeadTime.summaryStandard')}</span><span className="italic"> – {standardText}</span></p>
                <p><span className="font-semibold">{t('countryConfigurations.leadTimes.standardLeadTime.summaryFarDistance')}</span><span className="italic"> – {farDistanceText}</span></p>
              </div>
            )
          })()}

          <hr className="border-border" />
        </>
      )}

      {/* ──── Section 3: Non-Standard Lead Times ──── */}
      {nonStandard.length > 0 && (
        <>
          <SectionTitle
            titleKey="countryConfigurations.leadTimes.nonStandardLeadTimes.title"
            subtitle="countryConfigurations.leadTimes.nonStandardLeadTimes.subtitle"
          />
          {errors.nonStandardOptions && (
            <p role="alert" className="text-xs leading-4 text-negative">{errors.nonStandardOptions}</p>
          )}

          {nonStandard.map((ns, index) => {
            const dayName = getDayFullName(ns.day)
            const {standardText, farDistanceText} = buildSummaryText(
              'nonStandardLeadTimes',
              ns.baseLeadTime, ns.baseLeadTimeUomId, ns.baseLeadTimeUomDisplay,
              ns.referenceEvent, ns.farDistanceModifier, ns.farDistanceModifierUomId,
              ns.farDistanceModifierUomDisplay,
            )
            return (
              <div key={ns.day} className="flex flex-col gap-6" data-cy={`non-standard-day-${ns.day}`}>
                <h5 className="text-base font-bold text-foreground">{dayName}</h5>

                {/* Base lead time */}
                <div className="flex gap-8 items-start w-full">
                  <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
                    <div className="flex gap-0.5 items-center whitespace-nowrap">
                      <TextMedium className="text-foreground">
                        {t('countryConfigurations.leadTimes.nonStandardLeadTimes.baseLeadTime')}
                      </TextMedium>
                      <CountryConfigsTooltip
                        content={t('countryConfigurations.leadTimes.nonStandardLeadTimes.baseLeadTimeTooltip')}
                        delayMs={300}
                      >
                        <span className="text-foreground flex items-center cursor-default">
                          <Icon path={mdiInformationOutline} size="regular" />
                        </span>
                      </CountryConfigsTooltip>
                    </div>
                  </div>
                  <div className="grid grid-cols-[auto_auto_1fr] gap-1 items-start flex-1">
                    <NumericalInput
                      value={ns.baseLeadTime}
                      onChange={(val) => updateNonStandardField(index, 'baseLeadTime', val)}
                      min={0}
                      disabled={isReadOnly}
                      data-cy={`non-standard-${ns.day}-base-lead-time-value`}
                    />
                    <FilterDropdown
                      value={ns.baseLeadTimeUomId}
                      onChange={(val) => {
                        const opt = timeUomOptions.find((o) => o.value === val)
                        const updated = [...nonStandard]
                        updated[index] = {
                          ...updated[index],
                          baseLeadTimeUomId: val,
                          baseLeadTimeUomDisplay: opt?.label ?? val,
                        }
                        setValue('nonStandard', updated, {shouldDirty: true})
                      }}
                      options={timeUomOptions}
                      size="small"
                      disabled={isReadOnly}
                      data-cy={`non-standard-${ns.day}-base-lead-time-uom`}
                    />
                    <FilterDropdown
                      value={ns.referenceEvent}
                      onChange={(val) => updateNonStandardField(index, 'referenceEvent', val)}
                      options={referenceEventOptions}
                      size="medium"
                      disabled={isReadOnly}
                      data-cy={`non-standard-${ns.day}-reference-event`}
                    />
                  </div>
                </div>

                {/* Far distance modifier + Calculation method — shared width container */}
                <div className="flex flex-col gap-6 w-max">
                  {/* Far distance modifier */}
                  <div className="flex gap-8 items-start">
                    <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
                      <div className="flex gap-0.5 items-center whitespace-nowrap">
                        <TextMedium className="text-foreground">
                          {t('countryConfigurations.leadTimes.nonStandardLeadTimes.farDistanceModifier')}
                        </TextMedium>
                        <CountryConfigsTooltip
                          content={t('countryConfigurations.leadTimes.nonStandardLeadTimes.farDistanceModifierTooltip')}
                          delayMs={300}
                        >
                          <span className="text-foreground flex items-center cursor-default">
                            <Icon path={mdiInformationOutline} size="regular" />
                          </span>
                        </CountryConfigsTooltip>
                      </div>
                    </div>
                    <div className="flex gap-1 items-start">
                      <NumericalInput
                        value={ns.farDistanceModifier}
                        onChange={(val) => updateNonStandardField(index, 'farDistanceModifier', val)}
                        min={0}
                        disabled={isReadOnly}
                        data-cy={`non-standard-${ns.day}-far-distance-modifier-value`}
                      />
                      <FilterDropdown
                        value={ns.farDistanceModifierUomId}
                        onChange={(val) => {
                          const opt = timeUomOptions.find((o) => o.value === val)
                          const updated = [...nonStandard]
                          updated[index] = {
                            ...updated[index],
                            farDistanceModifierUomId: val,
                            farDistanceModifierUomDisplay: opt?.label ?? val,
                          }
                          setValue('nonStandard', updated, {shouldDirty: true})
                        }}
                        options={timeUomOptions}
                        size="small"
                        disabled={isReadOnly}
                        data-cy={`non-standard-${ns.day}-far-distance-modifier-uom`}
                      />
                    </div>
                  </div>

                  {/* Calculation method */}
                  <div className="flex gap-8 items-start w-full">
                    <div className="flex flex-col justify-center shrink-0 h-10 w-[180px]">
                      <div className="flex gap-0.5 items-center whitespace-nowrap">
                        <TextMedium className="text-foreground">
                          {t('countryConfigurations.leadTimes.nonStandardLeadTimes.calculationMethod')}
                        </TextMedium>
                        <CountryConfigsTooltip
                          content={t('countryConfigurations.leadTimes.nonStandardLeadTimes.calculationMethodTooltip')}
                          delayMs={300}
                        >
                          <span className="text-foreground flex items-center cursor-default">
                            <Icon path={mdiInformationOutline} size="regular" />
                          </span>
                        </CountryConfigsTooltip>
                      </div>
                    </div>
                    <div className="flex-1">
                      <FilterDropdown
                        value={ns.calculationMethod}
                        onChange={(val) => updateNonStandardField(index, 'calculationMethod', val)}
                        options={calculationMethodOptions}
                        size="medium"
                        disabled={isReadOnly}
                        data-cy={`non-standard-${ns.day}-calculation-method`}
                      />
                    </div>
                  </div>
                </div>

                {/* Non-standard day summary */}
                <div className="text-foreground text-base leading-6">
                  <p className="italic">{t('countryConfigurations.leadTimes.nonStandardLeadTimes.summaryLabel', {day: dayName})}</p>
                  <p><span className="font-semibold">{t('countryConfigurations.leadTimes.nonStandardLeadTimes.summaryStandard')}</span><span className="italic"> – {standardText}</span></p>
                  <p><span className="font-semibold">{t('countryConfigurations.leadTimes.nonStandardLeadTimes.summaryFarDistance')}</span><span className="italic"> – {farDistanceText}</span></p>
                </div>

                {index < nonStandard.length - 1 && <hr className="border-border" />}
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
